import { FC, useCallback, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Alert, Box, IconButton, Paper } from "@mui/material";
import BadgeIcon from "@mui/icons-material/Badge";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import PreviewIcon from "@mui/icons-material/Preview";
import {
  DataGridPro as DataGrid,
  getGridDateOperators,
  getGridNumericOperators,
  getGridStringOperators,
  GridActionsCell,
  GridActionsCellItem,
  GridColDef,
  GridFilterItem,
  GridFilterModel,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { format } from "date-fns";

import {
  dateOperators,
  dateTimeOperators,
  DEFAULT_PAGE_SIZE,
  numericOperator,
  statusesOptions,
  stringOperators,
} from "src/Pages/Company/SubscriptionBenefits/constans";
import { EventLogsSubscription } from "src/Pages/Company/SubscriptionBenefits/models";
import { useFetchSubscriptions } from "src/Pages/Company/SubscriptionBenefits/query";
import {
  parseFilterModel,
  parsePaginationModel,
  parseSortModel,
} from "src/Pages/Company/SubscriptionBenefits/utils";

import translateLabels from "src/utils/Translations/translateLabels";
import __ from "src/utils/Translations";
import { useGridStatePersistence } from "src/utils/hooks/datagrid/useGridStatePersistence";
import { useGridDataQuery } from "src/utils/hooks/datagrid/useGridDataQuery";
import { GridFilterDateInput } from "src/Components/DataGrid/GridFilterDateInput";
import SubscriptionBenefitsPreview from "./SubscriptionBenefitsPreview";
import { QueryParams } from "./api";

interface Props {
  companyId?: string;
  employeeId?: string;
}

// It's based on the table’s column types,
// update it if you change the columns
const DATE_COLUMNS = ["startDate", "resignationDate"];
const DATE_TIME_COLUMNS = ["createdAt", "canceledAt"];

// Builds the date / date-time filter operators from MUI's date operators,
// keeping only the allowed ones and swapping in our date input component.
const buildDateFilterOperators = (allowed: string[]) =>
  getGridDateOperators()
    .filter((operator) => allowed.includes(operator.value))
    .map((operator) => ({
      ...operator,
      InputComponent: GridFilterDateInput,
    }));

const SubscriptionBenefitsTable: FC<Props> = ({ companyId, employeeId }) => {
  const [preview, setPreview] = useState<{ id: string; open: boolean }>({
    id: "",
    open: false,
  });

  const apiRef = useGridApiRef();
  const { initialState } = useGridStatePersistence({
    storageKey: `event-logs-subscriptions-table:${companyId || ""}:${
      employeeId || ""
    }`,
    apiRef,
    defaultState: {
      pagination: {
        paginationModel: {
          pageSize: DEFAULT_PAGE_SIZE,
        },
      },
    },
  });

  const stringFilterOperators = useMemo(
    () =>
      getGridStringOperators().filter((operator) =>
        stringOperators.includes(operator.value)
      ),
    []
  );
  const numericFilterOperators = useMemo(
    () =>
      getGridNumericOperators().filter((operator) =>
        numericOperator.includes(operator.value)
      ),
    []
  );
  const dateTimeFilterOperators = useMemo(
    () => buildDateFilterOperators(dateTimeOperators),
    []
  );
  const dateFilterOperators = useMemo(
    () => buildDateFilterOperators(dateOperators),
    []
  );

  const scopeFilters = useMemo(() => {
    const items: GridFilterItem[] = [];
    if (companyId) {
      items.push({
        field: "company.id",
        operator: "equals",
        value: companyId,
      });
    }
    if (employeeId) {
      items.push({
        field: "employee.id",
        operator: "equals",
        value: employeeId,
      });
    }

    return items;
  }, [companyId, employeeId]);

  const parseFilters = useCallback(
    (model?: GridFilterModel) =>
      parseFilterModel(
        scopeFilters.concat(model?.items || []),
        DATE_COLUMNS,
        DATE_TIME_COLUMNS
      ),
    [scopeFilters]
  );

  const [queryParams, setQueryParams] = useState<QueryParams>();

  const { dataGridProps } = useGridDataQuery({
    initialState,
    setQueryParams,
    parsePaginationModel,
    parseSortModel,
    parseFilterModel: parseFilters,
  });

  const { data, isFetching, isError, error } =
    useFetchSubscriptions(queryParams);

  const rowCountRef = useRef(data?.totalElements || 0);
  const rowCount = useMemo(() => {
    if (data?.totalElements !== undefined) {
      rowCountRef.current = data.totalElements;
    }
    return rowCountRef.current;
  }, [data?.totalElements]);

  const columns = useMemo(
    () =>
      (
        [
          {
            headerName: __("ID"),
            field: "employee.id",
            valueGetter: (_value, row) => row.employee?.id,
            type: "string",
            filterOperators: stringFilterOperators,
            renderCell: ({ row, value }) => (
              <>
                {row.employee && row.company && (
                  <IconButton
                    component={RouterLink}
                    to={`/company/edit/${row.company.id}/employee-management/employees/${row.employee.id}#subscription_benefits`}
                    title={__("Przejdź do karty pracownika")}
                    size="small"
                  >
                    <BadgeIcon />
                  </IconButton>
                )}
                {value}
              </>
            ),
            width: 350,
          },
          {
            headerName: __("Imię"),
            field: "employee.firstName",
            valueGetter: (_value, row) => row.employee?.firstName,
            type: "string",
            filterOperators: stringFilterOperators,
            width: 150,
          },
          {
            headerName: __("Nazwisko"),
            field: "employee.lastName",
            valueGetter: (_value, row) => row.employee?.lastName,
            type: "string",
            filterOperators: stringFilterOperators,
            width: 150,
          },
          {
            headerName: __("E-mail służbowy"),
            field: "employee.businessEmail",
            valueGetter: (_value, row) => row.employee?.businessEmail,
            type: "string",
            filterOperators: stringFilterOperators,
            width: 150,
          },
          {
            headerName: __("E-mail prywatny"),
            field: "employee.privateEmail",
            valueGetter: (_value, row) =>
              row.employee?.privateEmail?.join(", "),
            type: "string",
            filterOperators: stringFilterOperators,
            width: 150,
          },
          {
            headerName: __("Skrócona nazwa firmy"),
            field: "company.shortName",
            valueGetter: (_value, row) => row.company?.shortName,
            type: "string",
            filterOperators: stringFilterOperators,
            width: 200,
          },
          {
            headerName: __("Id firmy"),
            field: "company.id",
            valueGetter: (_value, row) => row.company?.id,
            type: "string",
            filterOperators: stringFilterOperators,
            renderCell: (params) => (
              <>
                {params.row.company && (
                  <IconButton
                    component={RouterLink}
                    to={`/company/edit/${params.row.company.id}/subscription-benefits`}
                    title={__("Przejdź do profilu firmy")}
                    size="small"
                  >
                    <CorporateFareIcon />
                  </IconButton>
                )}
                {params.value}
              </>
            ),
          },
          {
            headerName: __("Numer pracownika"),
            field: "employee.fkNumber",
            valueGetter: (_value, row) => row.employee?.fkNumber,
            type: "string",
            filterOperators: stringFilterOperators,
            width: 150,
          },
          {
            headerName: __("Identyfikator abonamentu"),
            field: "id",
            type: "string",
            filterOperators: stringFilterOperators,
            width: 350,
          },
          {
            headerName: __("Status abonamentu"),
            field: "status",
            type: "singleSelect",
            valueOptions: translateLabels(statusesOptions),
            width: 150,
          },
          {
            headerName: __("Nazwa produktu"),
            field: "productName",
            type: "string",
            filterOperators: stringFilterOperators,
            width: 200,
          },
          {
            headerName: __("Data utworzenia"),
            field: "createdAt",
            type: "dateTime",
            filterOperators: dateTimeFilterOperators,
            valueGetter: (value) => value && new Date(value),
            renderCell: ({ value }) => (
              <span
                title={value ? format(value, "yyyy-MM-dd HH:mm:ss") : undefined}
              >
                {value && format(value, "yyyy-MM-dd")}
              </span>
            ),
            width: 150,
          },
          {
            headerName: __("Data anulowania"),
            field: "canceledAt",
            type: "dateTime",
            filterOperators: dateTimeFilterOperators,
            valueGetter: (value) => value && new Date(value),
            renderCell: ({ value }) => (
              <span
                title={value ? format(value, "yyyy-MM-dd HH:mm:ss") : undefined}
              >
                {value && format(value, "yyyy-MM-dd")}
              </span>
            ),
            width: 150,
          },
          {
            headerName: __("Data startu"),
            field: "startDate",
            type: "date",
            filterOperators: dateFilterOperators,
            valueGetter: (value) => value && new Date(value),
            valueFormatter: (value) =>
              value ? format(value, "yyyy-MM-dd") : "",
            width: 150,
          },
          {
            headerName: __("Data rezygnacji"),
            field: "resignationDate",
            type: "date",
            filterOperators: dateFilterOperators,
            valueGetter: (value) => value && new Date(value),
            valueFormatter: (value) =>
              value ? format(value, "yyyy-MM-dd") : "",
            width: 150,
          },
          {
            headerName: __("Priorytet"),
            field: "priority",
            type: "number",
            filterOperators: numericFilterOperators,
            width: 100,
          },
          {
            headerName: __("Powód rezygnacji"),
            field: "resignationReason",
            type: "string",
            filterOperators: stringFilterOperators,
            width: 200,
          },
          {
            headerName: __("Powód anulowania"),
            field: "cancellationReason",
            type: "string",
            filterOperators: stringFilterOperators,
            width: 200,
          },
          {
            headerName: __("Akcja"),
            field: "actions",
            type: "actions",
            align: "right",
            hideable: false,
            width: 60,
            renderCell: (params) => (
              <GridActionsCell {...params}>
                <GridActionsCellItem
                  icon={<PreviewIcon />}
                  onClick={() => setPreview({ open: true, id: params.row.id })}
                  label={__("Podgląd")}
                />
              </GridActionsCell>
            ),
          },
        ] as GridColDef<EventLogsSubscription>[]
      )
        .filter((col) => !employeeId || !col.field.includes("employee."))
        .filter((col) => !companyId || !col.field.includes("company.")),
    [
      companyId,
      dateFilterOperators,
      dateTimeFilterOperators,
      employeeId,
      numericFilterOperators,
      stringFilterOperators,
    ]
  );

  if (!initialState) {
    return null;
  }

  return (
    <Paper
      sx={{
        padding: "1rem",
      }}
    >
      {isError && (
        <Alert variant="outlined" color="warning">
          {String(error)}
        </Alert>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: 400,
        }}
      >
        <DataGrid
          {...dataGridProps}
          apiRef={apiRef}
          columns={columns}
          rows={data?.content || []}
          rowCount={rowCount}
          loading={isFetching}
          pinnedColumns={{ left: [], right: ["actions"] }}
          disableRowSelectionOnClick
          disableVirtualization
          disableEval
          showToolbar
          slotProps={{
            ...dataGridProps.slotProps,
            loadingOverlay: {
              variant: "linear-progress",
              noRowsVariant: "circular-progress",
            },
          }}
        />
      </Box>
      <SubscriptionBenefitsPreview
        subscriptionId={preview.id}
        open={preview.open}
        onClose={() => setPreview((p) => ({ ...p, open: false }))}
      />
    </Paper>
  );
};

export default SubscriptionBenefitsTable;
